import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { formatKr, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { BellRing, Send, FileDown, CheckCircle2, AlertTriangle, MailX, Settings2, Loader2, Info } from "lucide-react";

/** Intern etikett i listen. Inkassovarselet selv nummereres ikke – her vises bare hvilken gang det sendes. */
const levelLabel = (n, inkasso) => (inkasso ? (n > 1 ? `Inkassovarsel · ${n}. gang` : "Inkassovarsel") : `${n}. purring`);

function downloadBase64Pdf(b64, filename) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Faktura → Purringer: forslag til purringer for fakturaer som har passert forfall,
 * med valgbart gebyr/frist per faktura og sending/registrering av flere i én omgang.
 */
export default function SalesRemindersPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({}); // invoice_id -> true
  const [edits, setEdits] = useState({}); // invoice_id -> { fee, days }
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    setError("");
    return api.get("/sales/reminders/suggestions")
      .then((r) => {
        setData(r.data);
        const sel = {};
        const ed = {};
        for (const it of r.data.items) {
          sel[it.invoice.id] = true;
          ed[it.invoice.id] = { fee: it.suggested_fee, days: it.suggested_days };
        }
        setSelected(sel);
        setEdits(ed);
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => data?.items || [], [data]);
  const chosen = useMemo(() => items.filter((it) => selected[it.invoice.id]), [items, selected]);
  const chosenEmailable = chosen.filter((it) => it.can_email);
  const allSelected = items.length > 0 && chosen.length === items.length;
  const totalOutstanding = chosen.reduce((s, it) => s + Number(it.invoice.outstanding || 0), 0);
  const totalFees = chosen.reduce((s, it) => s + Number(edits[it.invoice.id]?.fee || 0), 0);

  const toggleAll = (v) => {
    const sel = {};
    if (v) for (const it of items) sel[it.invoice.id] = true;
    setSelected(sel);
  };
  const setEdit = (id, key, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [key]: value } }));

  const run = async (sendEmail) => {
    const list = sendEmail ? chosenEmailable : chosen;
    if (!list.length) return;
    setBusy(sendEmail ? "send" : "register");
    setResult(null);
    try {
      const payload = {
        send_email: sendEmail,
        items: list.map((it) => ({ invoice_id: it.invoice.id, fee: Number(edits[it.invoice.id]?.fee ?? 0), days: Math.max(1, Number(edits[it.invoice.id]?.days || 14)) })),
      };
      const r = await api.post("/sales/reminders/batch", payload);
      setResult({ ...r.data, sendEmail });
      if (r.data.pdf_base64 && !sendEmail) downloadBase64Pdf(r.data.pdf_base64, `Purringer_${new Date().toISOString().slice(0, 10)}.pdf`);
      if (r.data.failed_count === 0) toast.success(sendEmail ? `${r.data.ok_count} purring${r.data.ok_count === 1 ? "" : "er"} sendt på e-post` : `${r.data.ok_count} purring${r.data.ok_count === 1 ? "" : "er"} registrert – PDF lastes ned`);
      else toast.warning(`${r.data.ok_count} OK, ${r.data.failed_count} feilet`, { description: "Se detaljer under." });
      await load();
      window.dispatchEvent(new Event("rp:refresh-reminders"));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (error) return <EmptyState icon={AlertTriangle} title="Kunne ikke laste purringsforslag" description={error} actionLabel="Prøv igjen" onAction={load} testId="reminders-error" />;
  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-72" /><Skeleton className="h-64" /></div>;

  const s = data.settings || {};

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Faktura"
        title="Purringer"
        description={items.length ? `${items.length} ${items.length === 1 ? "faktura" : "fakturaer"} har passert forfall med mer enn ${s.reminder_suggest_after_days ?? 14} dager og er klar${items.length === 1 ? "" : "e"} for purring.` : "Ingen fakturaer trenger purring akkurat nå."}
        actions={<Button variant="outline" asChild><Link to="/profil#purring" data-testid="reminders-settings-link"><Settings2 className="h-4 w-4 mr-2" /> Innstillinger</Link></Button>}
      />

      {result ? (
        <Alert className={cn(result.failed_count ? "border-warn/40" : "border-success/40")} data-testid="reminders-result">
          {result.failed_count ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          <AlertTitle>{result.sendEmail ? "Purringer sendt" : "Purringer registrert"}</AlertTitle>
          <AlertDescription>
            <p>{result.ok_count} OK{result.failed_count ? `, ${result.failed_count} feilet` : ""}.{!result.sendEmail && result.ok_count ? " En samlet PDF med alle purringene er lastet ned." : ""}</p>
            <ul className="mt-2 space-y-1 text-xs">
              {result.results.map((r) => (
                <li key={r.invoice_id} className="flex items-center gap-2" data-testid={`reminders-result-${r.invoice_id}`}>
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <AlertTriangle className="h-3.5 w-3.5 text-flag" />}
                  <span>{r.ok ? `Faktura ${r.number_display}: ${levelLabel(r.level, s.reminder_inkasso_notice !== false)}${r.sent_to ? ` sendt til ${r.sent_to}` : " registrert"}` : `Faktura: ${r.error}`}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {items.length === 0 ? (
        <EmptyState icon={BellRing} title="Alt er à jour" description={`Fakturaer foreslås for purring når de har passert forfall med ${s.reminder_suggest_after_days ?? 14} dager (maks ${s.reminder_max_level ?? 3} purringer). Du kan endre dette under Firmaprofil → Purring.`} actionLabel="Se fakturaer" actionTo="/faktura/fakturaer" testId="reminders-empty" />
      ) : (
        <>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-base">Forslag</CardTitle>
                  <CardDescription>Juster gebyr og ny frist per faktura, velg hvilke som skal purres, og send alle i én omgang.{s.reminder_inkasso_notice !== false ? " Purringene sendes som inkassovarsel (inkassoloven § 9) med minst 14 dagers frist" : ""}{s.reminder_include_interest !== false && Number(s.late_interest_rate) > 0 ? `, og forsinkelsesrente ${s.late_interest_rate} % p.a. legges til beløpet.` : "."}</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button onClick={() => run(true)} disabled={!!busy || !data.email_configured || chosenEmailable.length === 0} data-testid="reminders-send-email-button">
                          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send {chosenEmailable.length} på e-post
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!data.email_configured ? <TooltipContent>E-post er ikke satt opp av administrator. Registrer og last ned PDF i stedet.</TooltipContent> : chosenEmailable.length < chosen.length ? <TooltipContent>{chosen.length - chosenEmailable.length} valgt{chosen.length - chosenEmailable.length === 1 ? "" : "e"} kunde{chosen.length - chosenEmailable.length === 1 ? "" : "r"} mangler e-post og hoppes over.</TooltipContent> : null}
                  </Tooltip>
                  <Button variant="outline" onClick={() => run(false)} disabled={!!busy || chosen.length === 0} data-testid="reminders-register-pdf-button">
                    {busy === "register" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />} Registrer {chosen.length} og last ned PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="border-t px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground bg-secondary/40">
                <label className="inline-flex items-center gap-2 text-sm text-foreground">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Velg alle" data-testid="reminders-select-all" /> Velg alle
                </label>
                <span data-testid="reminders-summary">Valgt: <strong className="text-foreground tnum">{chosen.length}</strong> · utestående <strong className="text-foreground tnum">{formatKr(totalOutstanding)}</strong> · gebyr <strong className="text-foreground tnum">{formatKr(totalFees)}</strong></span>
                {!data.email_configured ? <span className="inline-flex items-center gap-1"><Info className="h-3.5 w-3.5" /> E-post er ikke satt opp – purringer registreres og lastes ned som PDF.</span> : null}
              </div>
              <ul className="divide-y" data-testid="reminders-list">
                {items.map((it) => {
                  const inv = it.invoice;
                  const ed = edits[inv.id] || {};
                  const checked = !!selected[inv.id];
                  return (
                    <li key={inv.id} className={cn("px-4 sm:px-6 py-3 flex flex-col lg:flex-row lg:items-center gap-3", !checked && "opacity-60")} data-testid={`reminder-row-${inv.id}`}>
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <Checkbox checked={checked} onCheckedChange={(v) => setSelected((sel) => ({ ...sel, [inv.id]: !!v }))} aria-label={`Velg faktura ${inv.number_display}`} className="mt-1" data-testid={`reminder-select-${inv.id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link to={`/faktura/fakturaer/${inv.id}`} className="font-medium hover:underline truncate" data-testid={`reminder-invoice-link-${inv.id}`}>{inv.customer?.name || "Ukjent kunde"}</Link>
                            <Badge variant="outline" className="status-avvist border" data-testid={`reminder-level-${inv.id}`}>{levelLabel(it.level, s.reminder_inkasso_notice !== false)}</Badge>
                            {!it.can_email ? <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center gap-1 text-xs text-warn" data-testid={`reminder-noemail-${inv.id}`}><MailX className="h-3.5 w-3.5" /> mangler e-post</span></TooltipTrigger><TooltipContent>Kunden har ingen e-postadresse. Purringen kan registreres og lastes ned som PDF.</TooltipContent></Tooltip> : null}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Faktura {inv.number_display} · forfalt {formatDate(inv.due_date)} · <span className="text-flag">{it.days_overdue} dager over forfall</span>
                            {it.last_reminder_at ? ` · forrige purring ${formatDate(it.last_reminder_at)}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-end gap-3 lg:justify-end">
                        <div className="text-right mr-2">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Utestående</p>
                          <p className="font-medium tnum" data-testid={`reminder-outstanding-${inv.id}`}>{formatKr(inv.outstanding)}</p>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor={`fee-${inv.id}`}>Gebyr (kr)</label>
                          <Input id={`fee-${inv.id}`} type="number" min="0" step="1" className="h-9 w-24 tnum" value={ed.fee ?? 0} onChange={(e) => setEdit(inv.id, "fee", e.target.value)} disabled={!checked} data-testid={`reminder-fee-${inv.id}`} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] uppercase tracking-wide text-muted-foreground" htmlFor={`days-${inv.id}`}>Ny frist (dager)</label>
                          <Input id={`days-${inv.id}`} type="number" min="1" max="90" className="h-9 w-24 tnum" value={ed.days ?? 14} onChange={(e) => setEdit(inv.id, "days", e.target.value)} disabled={!checked} data-testid={`reminder-days-${inv.id}`} />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">Purregebyr kan tidligst kreves 14 dager etter forfall og maks 1/10 av inkassosatsen. Gebyr foreslås automatisk kun når dette er oppfylt; du kan alltid sette det til 0.</p>
        </>
      )}
    </div>
  );
}
